"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { Calendar, Building2, TrendingUp, CircleCheck as CheckCircle2, Clock, ShieldCheck, ArrowRight, Sparkles, Loader } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const benefits = [{
  icon: TrendingUp,
  title: "Data-Driven OTA Optimization",
  description: "Uncover hidden revenue leakage across major booking channels to increase RevPAR."
}, {
  icon: Building2,
  title: "Hospitality Tech Stack Audit",
  description: "Evaluate your PMS, channel manager, and rate shopping tools for maximum efficiency."
}, {
  icon: Clock,
  title: "Actionable Strategy Session",
  description: "Receive a tailored 30-minute strategic roadmap designed specifically for your property."
}];
function BookingFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 rounded-xl bg-accent/20 border border-primary/30">
        <div className="w-14 h-14 rounded-full bg-primary/20 text-primary flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-bold font-default text-card-foreground">
          Consultation Request Received
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {successMessage || "Thank you for reaching out. Sunil Singh will review your hotel details and connect with you within 24 hours."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="contactName" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-card-foreground">
                Your Name
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Rahul Sharma" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-card-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="rahul@hotel.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="hotelName" rules={{
        required: "Hotel name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-card-foreground">
                Hotel / Property Name
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Grand Resort & Spa" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-card-foreground">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+91 98765 43210" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="roomCount" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-card-foreground">
                Number of Rooms
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select room count" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="1-20">1 - 20 Rooms</SelectItem>
                  <SelectItem value="21-50">21 - 50 Rooms</SelectItem>
                  <SelectItem value="51-100">51 - 100 Rooms</SelectItem>
                  <SelectItem value="100+">100+ Rooms</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="primaryGoal" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-wider text-card-foreground">
                Primary Goal
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select primary goal" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="ota_optimization">OTA Revenue Growth</SelectItem>
                  <SelectItem value="tech_stack">Hospitality Tech Audit</SelectItem>
                  <SelectItem value="revenue_audit">Complete Revenue Audit</SelectItem>
                  <SelectItem value="direct_bookings">Increase Direct Bookings</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="notes" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-semibold uppercase tracking-wider text-card-foreground">
              Current Revenue Challenges or Notes
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your current occupancy, OTA performance, or specific goals..." className="min-h-20" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 shadow-md group transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-4 w-4" />
            Scheduling Consultation...
          </> : <>
            Book Free Revenue Strategy Session
            <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>

      <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <span>100% Confidential. No commitment required.</span>
      </div>
    </Form>;
}
export default function Consultation() {
  return <section id="consultationbookingcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 transition-colors relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-secondary text-secondary-foreground rounded-3xl p-6 sm:p-10 lg:p-14 shadow-2xl relative overflow-hidden border border-border/20">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center relative z-10">
            <div className="lg:col-span-6 space-y-8">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/15 border border-primary/30 text-primary text-xs font-semibold tracking-wide uppercase">
                <Sparkles className="w-3.5 h-3.5" />
                <span>1-on-1 Expert Revenue Strategy</span>
              </div>

              <div className="space-y-4">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif leading-tight text-secondary-foreground">
                  Ready to Maximize Your Hotel{"'"}s Revenue?
                </h2>
                <p className="text-base sm:text-lg text-secondary-foreground/80 leading-relaxed max-w-xl">
                  Book a direct strategy consultation with Sunil Singh, hospitality revenue specialist at HotelRevBaBa. Discover actionable OTA strategies designed to boost profitability and occupancy.
                </p>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-background/10 backdrop-blur-sm border border-secondary-foreground/10">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_singh_consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_singh_consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunil_singh_consultant.png-wvc-sizes" alt="Sunil Singh - Hotel Revenue Management Specialist" className="w-14 h-14 rounded-full object-cover border-2 border-primary" loading="lazy" />
                <div>
                  <h4 className="font-semibold text-secondary-foreground text-sm">
                    Sunil Singh
                  </h4>
                  <p className="text-xs text-secondary-foreground/70">
                    Lead Revenue Management Consultant, HotelRevBaBa
                  </p>
                  <p className="text-xs text-primary font-medium mt-0.5">
                    10+ Years Hospitality Expertise
                  </p>
                </div>
              </div>

              <div className="space-y-4 pt-2">
                {benefits.map((benefit, i) => {
                const Icon = benefit.icon;
                return <div key={benefit.title} data-index={i} className="flex items-start gap-3.5">
                      <div className="p-2 rounded-lg bg-primary/20 text-primary shrink-0 mt-1">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-secondary-foreground">
                          {benefit.title}
                        </h3>
                        <p className="text-xs text-secondary-foreground/75 leading-normal">
                          {benefit.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <div className="lg:col-span-6">
              <Card className="bg-card text-card-foreground border-border shadow-2xl rounded-2xl p-2 sm:p-4">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                      Book Your Consultation
                    </CardTitle>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-md">
                      <SafeHydrate><Calendar className="w-3.5 h-3.5" /></SafeHydrate>
                      Free Session
                    </span>
                  </div>
                  <CardDescription className="text-xs sm:text-sm text-muted-foreground">
                    Fill in your hotel details below to request a tailored revenue assessment.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <FormProvider formId="consultation-form-cta" sectionName="consultation-booking-cta" defaultValues={{
                  contactName: "",
                  email: "",
                  hotelName: "",
                  phone: "",
                  roomCount: "",
                  primaryGoal: "",
                  notes: ""
                }} validationRules={{
                  contactName: {
                    required: true,
                    minLength: 2
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  hotelName: {
                    required: true,
                    minLength: 2
                  },
                  phone: {
                    required: true
                  }
                }} submitText="Book Free Revenue Strategy Session" successMessage="Thank you for your request. Sunil Singh will get in touch with you shortly.">
                    <BookingFormInner />
                  </FormProvider>
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}